/**
 * Secret Field Plugin for MDXEditor
 * Main plugin export
 */

import {
  realmPlugin,
  addLexicalNode$,
  addImportVisitor$,
  addExportVisitor$,
  activeEditor$,
} from '@mdxeditor/editor';
import { Signal, withLatestFrom } from '@mdxeditor/gurx';
import { $insertNodes, $getSelection, $isRangeSelection } from 'lexical';
import { SecretFieldNode, $createSecretFieldNode } from './SecretFieldNode';
import { SecretFieldType } from './types';
import { SecretFieldVisitor } from './SecretFieldVisitor';
import { MdastSecretFieldVisitor } from './MdastSecretFieldVisitor';

// Export all components and utilities
export { SecretFieldNode, $createSecretFieldNode, $isSecretFieldNode } from './SecretFieldNode';
export { default as SecretFieldEditor } from './SecretFieldEditor';
export { default as SecretFieldView } from './SecretFieldView';
export * from './types';
export * from './secretFieldUtils';
export { setCurrentNoteId, getCurrentNoteId } from './secretFieldContext';

/**
 * Options for creating a secret field node
 */
export interface CreateSecretFieldOptions {
  label?: string;
  content?: string;
  fieldType?: SecretFieldType;
}

/**
 * A signal that inserts a new secret field into the editor.
 * @group Secret Field
 */
export const insertSecretField$ = Signal<Partial<CreateSecretFieldOptions>>((r) => {
  r.sub(
    r.pipe(
      insertSecretField$,
      withLatestFrom(activeEditor$)
    ),
    ([payload, editor]) => {
      if (!editor) return;

      editor.update(() => {
        const node = $createSecretFieldNode(
          payload.label || '', // Empty by default - starts in compact mode
          payload.content || '', // Empty by default - shows placeholder
          'inline' // Always inline
        );

        // Insert the node
        $insertNodes([node]);

        // Move selection after the inserted node
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          node.selectNext();
        }
      });
    }
  );
});

/**
 * Secret Field Plugin
 *
 * This plugin adds support for inline secret fields in MDXEditor:
 * - Format: ::secret[Label]{content}::
 *
 * Features:
 * - Create, edit, and delete secret fields
 * - Hide/reveal content
 * - Copy to clipboard
 * - Validation (max length)
 * - Optional label
 *
 * @example
 * ```tsx
 * import { MDXEditor } from '@mdxeditor/editor';
 * import { secretFieldPlugin } from './plugins/secretFieldPlugin';
 *
 * <MDXEditor
 *   markdown={content}
 *   plugins={[
 *     secretFieldPlugin(),
 *     // ...other plugins
 *   ]}
 * />
 * ```
 */
export const secretFieldPlugin = realmPlugin({
  init(realm) {
    realm.pubIn({
      [addLexicalNode$]: SecretFieldNode,
      [addImportVisitor$]: MdastSecretFieldVisitor,
      [addExportVisitor$]: SecretFieldVisitor,
    });
  }
});

/**
 * Plugin version
 */
export const PLUGIN_VERSION = '1.0.0';

